<template>
	<div id="galleryView" v-if="user">
		<router-view v-if="editMode" :notification-id="$route.params.notification_id" />
		<div v-else id="galleryWrapper">
			<section class="button-strip">
				<span>
					<h4>Templates</h4>
				</span>
				<span>
					<button
						type="button"
						class="button primary"
						@click="attemptToAddTemplate"
						:disabled="loading.db"
					>
						<img src="../assets/img/icons/gallery/baseline-add-24px-white.svg" />
						Add Template
					</button>
				</span>
			</section>
			<section ref="templateCardFlowContainer" :class="{ disabled: loading.db }" class="card-flow">
				<span v-for="notification in notifications" :key="notification.id" class="card notification">
					<div
						class="notification-preview"
						@click.stop="showNotificationDataModal(notification, 'api_data')"
					>
						<NotificationView
							id="notificationView"
							:notification="notification"
							:fitToParent="true"
							:class="{ grayscale: !notification.enabled }"
							:freeze_gifs="settings.templates.freeze_giphy_gifs"
						/>
						<div class="card-overlay">
							<div class="card-buttons">
								<img
									class="button-icon-card"
									src="../assets/img/icons/gallery/code-24px.svg"
									@click.stop="
                                        showNotificationDataModal(notification, 'api_data')
                                    "
									title="Automate"
								/>
								<Select
									:menu_style="{ width: '156px', 'max-height': '224px' }"
									:menu_offset="{ left: -116 }"
								>
									<template v-slot:trigger>
										<img
											class="button-icon-card"
											src="../assets/img/icons/gallery/kebab-24px.svg"
											title="Menu"
										/>
									</template>
									<template v-slot:options>
										<div @click.stop="editNotification(notification.id)">Edit</div>
										<div
											@click.stop="
                                                showNotificationDataModal(notification, 'api_data')
                                            "
										>Automate</div>
										<div
											@click.stop="
                                                showNotificationDataModal(notification, 'announce')
                                            "
										>Trigger</div>
										<div
											v-if="notification.enabled"
											@click.stop="
                                                setNotificationEnabled(notification, false)
                                            "
										>Turn off</div>
										<div v-else @click.stop="setNotificationEnabled(notification, true)">Turn on</div>
										<div @click.stop="duplicateNotification(notification.id)">Duplicate</div>
										<div
											v-if="$store.getters.devMode"
											@click.stop="saveAsTemplate(notification)"
										>Save as template</div>
										<div @click.stop="deleteNotificationHistory(notification.id)">Delete history</div>
										<div @click.stop="deleteNotification(notification.id)">Delete</div>
									</template>
								</Select>
							</div>
						</div>
					</div>
					<div class="notification-description">
						<span class="notification-name">{{ notification.name }}</span>
						<span
							class="button-toggle"
							:class="{ enabled: notification.enabled }"
							@click="setNotificationEnabled(notification, !notification.enabled)"
						/>
					</div>
				</span>
				<button class="notification-placeholder" @click="attemptToAddTemplate" :disabled="loading.db">
					<img src="../assets/img/icons/gallery/baseline-add-24px.svg" />
					<div>Add Template</div>
				</button>
			</section>
		</div>
		<transition name="fade">
			<Alert v-if="alert.active" danger @input="alert.oninput($event)">
				<template v-slot:title>{{ alert.title }}</template>
				<template v-slot:description>{{ alert.description }}</template>
				<template v-slot:secondary-action>{{ alert.actions.secondary }}</template>
				<template v-slot:primary-action>{{ alert.actions.primary }}</template>
			</Alert>
		</transition>
		<transition name="fade">
			<NotificationDataModal
				v-if="notification_data_modal.active"
				:notification="notification_data_modal.notification"
				:start_tab="notification_data_modal.tab"
				@dismiss="notification_data_modal.active = false"
			/>
		</transition>
		<transition name="zoom-fade">
			<Banner
				v-if="$_banner_active"
				class="error"
				style="top: 72px"
				@actionClick="banner.onaction"
				@dismiss="$_banner_hide"
			>
				<template v-slot:message>{{ banner.message }}</template>
				<template v-slot:action>{{ banner.action }}</template>
			</Banner>
		</transition>
	</div>
</template>

<script>
import Alert from "./overlay/alert";
import { Promise } from "q";
import Notification from "../modules/notification";
import NotificationView from "./notification-view";
import Select from "./input/select";
import NotificationDataModal from "./overlay/notification-data-modal";
import Banner from "./overlay/banner";
import BannerMixin from "@/mixins/banner-mixin";
import cloneDeep from "lodash/cloneDeep";

export default {
	data() {
		return {
			loading: {
				db: false
			},
			editor_open: false,
			alert: {
				active: false,
				title: "All good?",
				oninput: null
			},
			notification_data_modal: {
				active: false,
				notification: null,
				tab: null
			}
		};
	},
	mixins: [BannerMixin],
	components: {
		Alert,
		NotificationView,
		Select,
		NotificationDataModal,
		Banner
	},
	computed: {
		user() {
			return this.$store.getters.user;
		},
		notifications() {
			return this.$store.getters.notifications;
		},
		editMode() {
			return this.$route.params.notification_id !== undefined;
		},
		canAddMoreTemplates() {
			return this.validateNotificationStates();
		},
		settings() {
			return this.$store.getters.settings;
		}
	},
	watch: {
		notifications(value) {
			if (!this.user.onboarding_complete && value.length > 0) {
				this.$nextTick(() => {
					this.$emit("showOnboardingTooltip", {
						elementReference: this.$refs.templateCardFlowContainer
							.children[0],
						ondismiss: () => {
							this.$emit("updateUserData", {
								onboarding_complete: true
							});

							this.$ga.event({
								eventCategory: "app-usage",
								eventAction: "onboarding-complete"
							});
						}
					});
				});
			}
		}
	},
	methods: {
		attemptToAddTemplate() {
			if (this.canAddMoreTemplates) {
				this.addNotification();
			} else {
				this.$_banner_show({
					message:
						"The Basic plan is limited to a single active notification template only.",
					action: "See pricing options",
					onaction: () => this.$emit("showPricingPlans")
				});
			}
		},
		async addNotification() {
			this.loading.db = true;

			const templateDoc = await this.$db()
				.collection("notification_templates")
				.doc("default")
				.get();
			let notification = null;

			if (templateDoc.exists) {
				notification = new Notification({
					author: this.user.uid,
					...cloneDeep(templateDoc.data())
				});

				notification.initializeLayerData();
			} else {
				notification = new Notification({
					name: "Untitled template",
					background_color: "#fff",
					author: this.user.uid
				});
			}

			const ref = await this.$db()
				.collection("notifications")
				.add(notification.dataAsObject);

			await this.$db()
				.collection("users")
				.doc(this.user.uid)
				.update({
					notification_ids: this.$db.FieldValue.arrayUnion(ref.id)
				});

			this.editNotification(ref.id);

			this.loading.db = false;

			this.$analytics.logEvent("notification_created");
			this.$ga.event({
				eventCategory: "app-usage",
				eventAction: "template-created"
			});
		},
		async saveAsTemplate(notification) {
			await this.$db()
				.collection("notification_templates")
				.doc("default")
				.update({
					name: notification.name,
					template: notification.template.map(
						layer => layer.dataAsObject
					),
					enabled: notification.enabled,
					background_color: notification.background_color,
					sound: notification.sound
				});
		},
		async duplicateNotification(notificationId) {
			this.loading.db = true;

			let notification = this.notifications.find(
				n => n.id === notificationId
			);
			const notificationClone = new Notification(notification);
			const now = Date.now();

			notificationClone.date_created = now;
			notificationClone.date_edited = now;

			const ref = await this.$db()
				.collection("notifications")
				.add(notificationClone.dataAsObject);

			await this.$db()
				.collection("users")
				.doc(this.user.uid)
				.update({
					notification_ids: this.$db.FieldValue.arrayUnion(ref.id)
				});

			this.$emit("refreshNotificationData");

			this.loading.db = false;

			this.$analytics.logEvent("notification_duplicated");
		},
		async deleteNotification(notificationId) {
			const userResponse = await this.promptUser({
				title: "Delete notification template",
				description: "Are you sure you want to delete this template?",
				actions: {
					primary: "Delete",
					secondary: "Cancel"
				}
			});
			if (userResponse === false) return;

			this.loading.db = true;

			await this.$db()
				.collection("notifications")
				.doc(notificationId)
				.delete();

			await this.$db()
				.collection("users")
				.doc(this.user.uid)
				.update({
					notification_ids: this.$db.FieldValue.arrayRemove(
						notificationId
					),
					deleted_notification_ids: this.$db.FieldValue.arrayUnion(
						notificationId
					)
				});

			this.$emit("refreshNotificationData");

			this.loading.db = false;

			this.$analytics.logEvent("notification_deleted");
		},
		async setNotificationEnabled(notification, state) {
			if (state) {
				const permit = this.validateNotificationStates(); // Check if action is permitted by user's plan limits
				if (!permit) {
					this.$_banner_show({
						message:
							"The Basic plan is limited to a single active notification template only.",
						action: "See pricing options",
						onaction: () => this.$emit("showPricingPlans")
					});
					return;
				}
			}

			notification.enabled = state;

			await this.$db()
				.collection("notifications")
				.doc(notification.id)
				.update({
					enabled: notification.enabled,
					date_edited: Date.now()
				});

			console.log("Notification saved."); // eslint-disable-line no-console
		},
		validateNotificationStates() {
			if (
				this.user &&
				this.user.plan_data &&
				this.user.plan_data.limits.templates
			) {
				const numEnabledNotifications = this.notifications.filter(
					n => n.enabled
				).length;
				if (
					numEnabledNotifications >=
					this.user.plan_data.limits.templates
				) {
					return false;
				} else return true;
			} else return true;
		},
		editNotification(notificationId) {
			this.$router.push({
				name: "edit",
				params: { notification_id: notificationId }
			});
		},
		async deleteNotificationHistory(notificationId) {
			const userResponse = await this.promptUser({
				title: "Delete notification history",
				description:
					"Are you sure you want to delete all history for this notification?",
				actions: {
					primary: "Delete",
					secondary: "Cancel"
				}
			});
			if (userResponse === false) return;

			this.loading.db = true;

			const response = await this.$http({
				method: "delete",
				url: "https://api.gleebeam.com/v1/history",
				data: {
					notification_id: notificationId
				}
			});

			console.log("Notification history deleted.", response.data.message); // eslint-disable-line no-console

			this.loading.db = false;
		},
		promptUser(alertData) {
			// cancel previous prompt in case there is one already active
			if (this.alert.oninput) this.alert.oninput(false);

			return new Promise(resolve => {
				this.alert.oninput = response => {
					this.alert.active = false;
					this.alert.oninput = null;
					resolve(response);
				};

				this.alert = Object.assign(this.alert, alertData);
				this.alert.active = true;
			});
		},
		showNotificationDataModal(notification, tab) {
			this.$emit("dismissOnboardingTooltip");

			this.notification_data_modal.notification = notification;
			this.notification_data_modal.tab = tab;
			this.notification_data_modal.active = true;
		}
	}
};
</script>

<style scoped>
#notificationView {
	transform-origin: top left;
}

.card-flow {
	padding: 32px 0 32px 32px;
}

.card-buttons {
	/* display: flex; */
	float: right;
	margin: 8px;
}
.card-buttons > * {
	position: relative;
	display: inline-block;
	margin-left: 8px;
}

.grayscale {
	filter: grayscale(1);
}

.notification-placeholder {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-flow: column;

	vertical-align: top;

	margin-right: 32px;
	margin-bottom: 32px;

	border-radius: 4px;

	width: 480px;
	height: 295px;

	border: 1px dashed var(--gray);
	
	font-weight: 600;
	font-size: 16px;
	line-height: 22px;
	color: var(--darker-black);

	cursor: pointer;
}

.notification-placeholder > img {
	margin-bottom: 8px;
}

.notification-placeholder:disabled {
	pointer-events: none;

	cursor: default;

	color: gray;
}
</style>
